import { FunctionComponent, useContext, useEffect, useState } from 'react'
import { ReportPeriodContext } from '../../../context/report-periods/report-periods'
import { Box, FormControl, MenuItem, Select, SelectChangeEvent, Stack, Typography } from '@mui/material';
import { IReportPeriodResponse } from '../../../shared/services/report-period/report-period.model';
import { CalendarMonth, Lock } from '@mui/icons-material';

export const ReportPeriodSelectComponent: FunctionComponent<{
  onlyOpenReports?: boolean,
  onlyLockedReports?: boolean,
  size?: 'small' | 'medium',
  disabled?: boolean,
  // Optional controlled mode: when both are provided the selection is local to the caller and does
  // NOT change the app-wide report period. Omit them for the default context-bound behaviour.
  value?: string,
  onChange?: (value: string) => void,
}> = ({ onlyOpenReports, onlyLockedReports, size = 'medium', disabled = false, value, onChange }) => {

  const reportPeriodContext = useContext(ReportPeriodContext);
  const [requiredReportPeriods, setRequiredReportPeriods] = useState<IReportPeriodResponse[]>([])

  useEffect(() => {
    if (onlyLockedReports) {
      setRequiredReportPeriods(reportPeriodContext.lockedReportPeriods);
    } else if (onlyOpenReports) {
      setRequiredReportPeriods(reportPeriodContext.openReportPeriods);
    } else {
      setRequiredReportPeriods(reportPeriodContext.allReportPeriods);
    }
  }, [
    onlyOpenReports,
    onlyLockedReports,
    reportPeriodContext.allReportPeriods,
    reportPeriodContext.openReportPeriods,
    reportPeriodContext.lockedReportPeriods
  ])

  const handleSelect = (event: SelectChangeEvent<string>) => {
    const selected = event.target.value;
    // Controlled/local mode: report the change to the caller only, leaving the app-wide period alone.
    if (onChange) {
      onChange(selected);
      return;
    }
    if (onlyOpenReports) {
      reportPeriodContext.setLastSelectedOpenReportId(selected);
      reportPeriodContext.setLastSelectedReportId(selected);
    } else if (onlyLockedReports) {
      reportPeriodContext.setLastSelectedLockedReportId(selected);
      reportPeriodContext.setLastSelectedReportId(selected);
    } else {
      const reportIsLocked = requiredReportPeriods.find(report => report.id === selected)?.locked;
      reportPeriodContext.setLastSelectedReportId(selected);
      if (reportIsLocked) {
        reportPeriodContext.setLastSelectedLockedReportId(selected);
      } else {
        reportPeriodContext.setLastSelectedOpenReportId(selected);
      }
    }
  }


  return (
    <>
      {requiredReportPeriods.length > 0 && <Box>
        <FormControl fullWidth variant='outlined'>
          <Select
            size={size}
            id='reportPeriod' name='reportPeriod'
            value={value !== undefined ? value : (onlyOpenReports ? reportPeriodContext.lastSelectedOpenReportId : onlyLockedReports ? reportPeriodContext.lastSelectedLockedReportId : reportPeriodContext.lastSelectedReportId)}
            onChange={handleSelect}
            placeholder='Select report period'
            disabled={requiredReportPeriods?.length < 2 || disabled}
            sx={{ color: 'text.primary', minWidth: '180px' }}>
            {requiredReportPeriods?.sort((a, b) => Number(a.calendarYear) - Number(b.calendarYear)).map((period, index) =>
              <MenuItem key={index} value={period.id}>
                <Stack sx={{ display: 'flex', alignItems: 'center', flexDirection: 'row', width: '100%' }}>
                  <CalendarMonth sx={{ color: 'text.secondary', mr: 1 }} />
                  <Stack sx={{ display: 'flex', alignItems: 'center', flexDirection: 'row', width: '100%', justifyContent: 'space-between' }}>
                    <Typography>{period.friendlyName}</Typography>
                    {period.locked && <Lock sx={{ color: 'text.secondary', fontSize: '0.9rem' }} />}
                  </Stack>
                </Stack>
              </MenuItem>
            )}
          </Select>
        </FormControl>
      </Box>}
    </>

  )
}